import type { ReactNode } from 'react';
import { ApiError, type HuntOrder } from '../../api/types';
import { api } from '../../api';
import { t } from '../../i18n/idioma';
import { rotuloTecla, teclaDe } from '../../input/atalhos';
import { LinhaPop, MenuPop } from './MenuPop';
import { ItemIcon } from '../ItemIcon';
import type { PopId } from './BarraTopo';
import type { DummyTrainSkill } from '../../data/armas';
import { nomeDaSkill } from '../../i18n/nomes';

export function CartaoTrava({
  titulo, lugar, cidade, onVoltar, onVer
}: {
  titulo: string;
  lugar: string;
  cidade: string;
  onVoltar(): void;
  onVer(): void;
}) {
  return (
    <div className="travado">
      <b>{titulo}</b>
      <span>{t('hud.trava.texto', { lugar, cidade })}</span>
      <div className="p-row">
        <button type="button" className="btn btn--primary btn--tiny" onClick={onVoltar}>{t('hud.trava.voltar', { cidade })}</button>
        <button type="button" className="btn btn--ghost btn--tiny" onClick={onVer}>{t('hud.trava.verCacada')}</button>
      </div>
    </div>
  );
}

function casca(id: PopId, titulo: string, x: number, onFechar: () => void, children: ReactNode, esc = true) {
  return <MenuPop id={id} titulo={titulo} x={x} onFechar={onFechar} esc={esc}>{children}</MenuPop>;
}

/** A linha "Vender itens" tem o selo "novo" até a primeira abertura (só neste navegador). */
const CHAVE_VENDER_VISTO = 'uoidle.vender.visto';

function venderJaVisto(): boolean {
  try { return localStorage.getItem(CHAVE_VENDER_VISTO) === '1'; } catch { return true; }
}

function marcarVenderVisto() {
  try { localStorage.setItem(CHAVE_VENDER_VISTO, '1'); } catch { /* navegador sem storage: o selo fica */ }
}

/**
 * O menu Lojas (compras da cidade, Renato, 29/10/2026): só Comprar e Vender itens. Os vendedores saíram daqui — a
 * janela Comprar junta tudo o que eles vendem, com filtro por loja, e cada NPC continua atendendo no balcão do mapa.
 */
export function PopLojas({
  x, cidade, cacando, ordem, natal, esc = true, onFechar, onComprar, onVender, onVoltar, onCacar
}: {
  x: number;
  cidade: string;
  cacando: boolean;
  ordem: HuntOrder | null;
  natal: string;
  esc?: boolean;
  onFechar(): void;
  onComprar(): void;
  onVender(): void;
  onVoltar(): void;
  onCacar(): void;
}) {
  const novo = !venderJaVisto();
  const venderBotao = (
      <button
        type="button"
        className="mkt-ci vd-entrada"
        onClick={() => { marcarVenderVisto(); onVender(); }}
      >
        <ItemIcon id="silver" size={28} eager />
        <span>
          <span className="mkt-ci__n">{t('hud.lojas.vender')}{novo && <span className="vd-novo">{t('hud.lojas.novo')}</span>}</span>
          <span className="mkt-ci__m">{cacando ? t('hud.lojas.venderCacando') : t('hud.lojas.venderMeta')}</span>
        </span>
        <span className="mkt-ci__p"><b>›</b></span>
      </button>
  );
  const vender = <div className="mkt-lista">{venderBotao}</div>;
  const titulo = cacando ? t('hud.menu.lojas') : t('hud.lojas.titulo', { cidade });
  return casca('lojas', titulo, x, onFechar, (
    <>
      {cacando && ordem ? (
        <>
          {vender}
          <CartaoTrava
            titulo={t('hud.trava.lojas')}
            lugar={t(`cacada.${ordem.huntId}.nome`)}
            cidade={natal}
            onVoltar={onVoltar}
            onVer={onCacar}
          />
        </>
      ) : (
        <div className="mkt-lista">
          <button type="button" className="mkt-ci vd-entrada cc-entrada" onClick={onComprar}>
            <span className="cc-entrada__ic" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"><path d="M5 8h14l-1 12H6Z" /><path d="M9 8V6a3 3 0 0 1 6 0v2" /></svg>
            </span>
            <span>
              <span className="mkt-ci__n">{t('hud.lojas.comprar')}<span className="vd-novo">{t('hud.lojas.novo')}</span></span>
              <span className="mkt-ci__m">{t('hud.lojas.comprarMeta', { cidade })}</span>
            </span>
            <span className="mkt-ci__p"><b>›</b></span>
          </button>
          {venderBotao}
        </div>
      )}
    </>
  ), esc);
}

const OFICIOS: ReadonlyArray<{ id: 'ferreiro' | 'alfaiate' | 'arqueiro'; icone: string; nome: string; meta: string }> = [
  { id: 'ferreiro', icone: 'iron-ingot', nome: 'criacao.ferreiro', meta: 'hud.oficio.ferreiro' },
  { id: 'alfaiate', icone: 'bandage', nome: 'criacao.alfaiate', meta: 'hud.oficio.alfaiate' },
  { id: 'arqueiro', icone: 'shaft', nome: 'criacao.arqueiro', meta: 'hud.oficio.arqueiro' }
];

export function PopOficios({
  x, cacando, ordem, natal, esc = true, bonus, onFechar, onAbrir, onVoltar, onCacar
}: {
  x: number;
  /** Os ofícios com o bônus da casa valendo aqui (techspec-marceneiro-e-oficios-em-casa §05.4): o selo na linha. */
  bonus?: ReadonlySet<string>;
  cacando: boolean;
  ordem: HuntOrder | null;
  natal: string;
  esc?: boolean;
  onFechar(): void;
  onAbrir(id: 'ferreiro' | 'alfaiate' | 'arqueiro'): void;
  onVoltar(): void;
  onCacar(): void;
}) {
  return casca('oficios', t('hud.menu.oficios'), x, onFechar, cacando && ordem ? (
    <CartaoTrava
      titulo={t('hud.trava.oficios')}
      lugar={t(`cacada.${ordem.huntId}.nome`)}
      cidade={natal}
      onVoltar={onVoltar}
      onVer={onCacar}
    />
  ) : (
    <div className="mkt-lista">
      {OFICIOS.map(o => (
        <LinhaPop key={o.id} defId={o.icone} nome={t(o.nome)}
          meta={bonus?.has(o.id) ? `${t(o.meta)} · ✦ ${t('oficio.casa.selo')}` : t(o.meta)}
          destaque={bonus?.has(o.id)} dir="›" onClick={() => onAbrir(o.id)} />
      ))}
    </div>
  ), esc);
}

/** As skills que o manequim de treino ensina, com a arma que representa cada uma. */
const TREINOS: ReadonlyArray<{ skill: DummyTrainSkill; icone: string; imagem?: string }> = [
  { skill: 'swordsmanship', icone: 'katana' },
  { skill: 'maceFighting', icone: 'gnarled-staff' },
  { skill: 'fencing', icone: 'dagger' },
  { skill: 'axing', icone: 'large-battle-axe' },
  /* o livro de magias do próprio UO (arte 0x0EFA do cliente), não o ícone genérico */
  { skill: 'magery', icone: 'spellbook', imagem: '/sprites/itens/hud/spellbook.png' }
];

/**
 * Treinar (techspec-hud-v3 §24): o mesmo menu do botão direito no manequim, aberto pelo topo. Escolhida a skill, o
 * personagem anda até o manequim de treino mais perto e começa (quem faz isso é o WorldScreen, `onEscolher`).
 */
export function PopTreinar({
  x, cacando, ordem, natal, esc = true, travado, bonus, onFechar, onEscolher, onVoltar, onCacar
}: {
  x: number;
  cacando: boolean;
  ordem: HuntOrder | null;
  natal: string;
  esc?: boolean;
  /** Dentro da casa sem Boneco de Punguista (techspec-marceneiro-e-oficios-em-casa §06): o menu fica travado. */
  travado?: string;
  /** Dentro da casa com o boneco: a linha do bônus no alto. */
  bonus?: string;
  onFechar(): void;
  onEscolher(skill: DummyTrainSkill): void;
  onVoltar(): void;
  onCacar(): void;
}) {
  return casca('treinar', t('manequim.treinoTitulo'), x, onFechar, travado ? (
    <p className="pop-trava-casa" role="status">🔒 {travado}</p>
  ) : cacando && ordem ? (
    <CartaoTrava
      titulo={t('hud.trava.treinar')}
      lugar={t(`cacada.${ordem.huntId}.nome`)}
      cidade={natal}
      onVoltar={onVoltar}
      onVer={onCacar}
    />
  ) : (
    <div className="mkt-lista">
      {bonus && <p className="pop-bonus-casa">✦ {bonus}</p>}
      {TREINOS.map(o => (
        <LinhaPop key={o.skill} defId={o.icone} imagem={o.imagem} nome={nomeDaSkill(o.skill)}
          meta={t(o.skill === 'magery' ? 'hud.treinar.magia' : 'hud.treinar.arma')} dir="›" onClick={() => onEscolher(o.skill)} />
      ))}
    </div>
  ), esc);
}

export function PopMais({
  x, modoCel, compacto, minimapa, ehStaff, online, teclaConfig, teclaGm, suportePede, correioNovas = 0, indicar = false,
  onFechar, onJanela, onMinimapa, onGuia
}: {
  x: number;
  modoCel: boolean;
  compacto: boolean;
  minimapa: boolean;
  ehStaff: boolean;
  online: number;
  teclaConfig: string;
  teclaGm: string;
  /** Chamados de suporte esperando uma resposta do jogador. */
  suportePede: number;
  /** Cartas não lidas no correio (techspec-correio §06): a linha Correio, a primeira, fica dourada. */
  correioNovas?: number;
  /** Mostra Indicar amigos (techspec-indicacoes §04): some quando o console desliga. */
  indicar?: boolean;
  onFechar(): void;
  onJanela(id: 'perfil' | 'dano' | 'config' | 'gm' | 'suporte' | 'rankings' | 'evento' | 'correio' | 'indicar'): void;
  onMinimapa(): void;
  /** Abre de novo o guia de boas-vindas (techspec-boas-vindas). */
  onGuia?(): void;
}) {
  return casca('mais', t('hud.menu.mais'), x, onFechar, (
    <>
      <div className="mkt-lista">
        <LinhaPop
          svg="correio"
          nome={t('correio.titulo')}
          meta={correioNovas > 0 ? t(correioNovas > 1 ? 'correio.mais.novas' : 'correio.mais.nova', { n: correioNovas }) : t('correio.mais.desc')}
          destaque={correioNovas > 0}
          pip={correioNovas}
          classe={correioNovas > 0 ? 'co-mais' : undefined}
          dir="›"
          onClick={() => onJanela('correio')}
        />
        {indicar && (
          <LinhaPop svg="indicar" nome={t('indicacoes.titulo')} meta={t('indicacoes.maisDesc')} dir="›" onClick={() => onJanela('indicar')} />
        )}
        {modoCel && (
          <LinhaPop svg="perfil" nome={t('perfil.titulo')} meta={t('hud.mais.perfil')} dir="›" onClick={() => onJanela('perfil')} />
        )}
        {compacto && (
          <LinhaPop
            svg="viajar"
            nome={t(minimapa ? 'hud.mais.esconderMinimapa' : 'hud.mais.minimapa')}
            meta={t('hud.mais.minimapaDesc')}
            dir="›"
            onClick={onMinimapa}
          />
        )}
        {/* No PC e no tablet Eventos, Rankings e Dano moram no topo (techspec-hud-v3 §18); no celular o topo some
            e eles voltam para cá. */}
        {modoCel && (
          <>
            <LinhaPop
              svg="evento"
              nome={t('evento.mais.titulo')}
              meta={t('evento.mais.desc')}
              dir="›"
              onClick={() => onJanela('evento')}
            />
            <LinhaPop
              svg="rankings"
              nome={t('rankings.janela')}
              meta={t('rankings.maisDesc')}
              dir="›"
              onClick={() => onJanela('rankings')}
            />
            <LinhaPop svg="dano" nome={t('analise.titulo')} meta={t('analise.hudMais')} dir="›" onClick={() => onJanela('dano')} />
          </>
        )}
        {onGuia && <LinhaPop svg="guia" nome={t('guia.mais')} meta={t('guia.maisMeta')} dir="›" onClick={onGuia} />}
        {/* A Wiki é do site: abre em outra aba, sem fechar o jogo (techspec-wiki §07). */}
        <LinhaPop
          svg="wiki"
          nome={t('hud.mais.wiki')}
          meta={t('hud.mais.wikiDesc')}
          dir="↗"
          onClick={() => { window.open('/wiki', '_blank', 'noopener,noreferrer'); onFechar(); }}
        />
        {/* No PC a Configuração tem botão no topo; no celular (sem topo) ela continua aqui. */}
        {modoCel && (
          <LinhaPop svg="config" nome={t('opcoes.titulo')} meta={t('hud.mais.config', { tecla: teclaConfig })} dir="›" onClick={() => onJanela('config')} />
        )}
        <LinhaPop
          svg="suporte"
          nome={t('suporte.janela')}
          meta={suportePede > 0 ? t('suporte.maisPede', { n: suportePede }) : t('suporte.maisDesc')}
          destaque={suportePede > 0}
          dir="›"
          onClick={() => onJanela('suporte')}
        />
        {ehStaff && (
          <LinhaPop svg="gm" nome={t('gm.aba')} meta={t('hud.mais.gm', { tecla: teclaGm })} dir="›" onClick={() => onJanela('gm')} />
        )}
      </div>
      <p className="p-sub">{t('hud.mais.online', { n: online })}</p>
    </>
  ));
}

export function PopCidade({
  x, cidade, cacando, onFechar, onPop, onBanco, onMercado
}: {
  x: number;
  cidade: string;
  cacando: boolean;
  onFechar(): void;
  onPop(id: PopId): void;
  onBanco(): void;
  onMercado(): void;
}) {
  const trava = (id: string) => cacando && (id === 'lojas' || id === 'banco' || id === 'oficios' || id === 'treinar');
  const linhas: ReadonlyArray<{ id: string; svg: string; nome: string; meta: string; pop?: PopId }> = [
    { id: 'lojas', svg: 'lojas', nome: 'hud.menu.lojas', meta: 'hud.cidade.lojas', pop: 'lojas' },
    { id: 'banco', svg: 'banco', nome: 'banco.titulo', meta: 'hud.cidade.banco' },
    { id: 'mercado', svg: 'mercado', nome: 'hud.menu.leilao', meta: 'hud.cidade.mercado' },
    { id: 'oficios', svg: 'oficios', nome: 'hud.menu.oficios', meta: 'hud.cidade.oficios', pop: 'oficios' },
    { id: 'treinar', svg: 'treinar', nome: 'manequim.treinoTitulo', meta: 'hud.cidade.treinar', pop: 'treinar' }
  ];
  return casca('cidade', t('hud.nav.cidade'), x, onFechar, (
    <div className="mkt-lista">
      {linhas.map(l => {
        const travada = trava(l.id);
        return (
          <LinhaPop
            key={l.id}
            svg={l.svg}
            nome={t(l.nome)}
            meta={travada ? t('hud.soNaCidade') : t(l.meta, { cidade })}
            dir={travada ? '🔒' : '›'}
            onClick={() => {
              if (l.id === 'banco') onBanco();
              else if (l.id === 'mercado') onMercado();
              else if (l.pop) onPop(l.pop);
            }}
          />
        );
      })}
    </div>
  ));
}

export function PopSocial({
  x, on, n, naoLidas, onFechar, onGuilda, onAmigos
}: {
  x: number;
  on: number;
  n: number;
  naoLidas: number;
  onFechar(): void;
  onGuilda(): void;
  onAmigos(): void;
}) {
  return casca('social', t('hud.nav.social'), x, onFechar, (
    <div className="mkt-lista">
      <LinhaPop svg="guilda" nome={t('guilda.titulo')} meta={t('hud.social.guilda')} dir="›" onClick={onGuilda} />
      <LinhaPop
        svg="amigos"
        nome={t('amigo.titulo')}
        meta={t('hud.social.amigos', { on, n })}
        dir={naoLidas > 0 ? <span className="kit-pip">{naoLidas}</span> : '›'}
        onClick={onAmigos}
      />
    </div>
  ));
}

export async function pararCacada(onOrdem: (ordem: HuntOrder | null) => void, avisar: (texto: string) => void) {
  try {
    const board = await api.cancelHunt();
    onOrdem(board.ordem);
  } catch (err) {
    avisar(err instanceof ApiError ? err.message : t('comum.recusouPedido'));
  }
}

export function teclaDeAcao(acao: 'config' | 'gm'): string {
  return rotuloTecla(teclaDe(acao));
}
